import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { PeriodMonthColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager-month';
import HotelPricingAssistantTableTooltipsTooltipRoomLeftToSell from 'lumen/components/hotel/pricing-assistant/table/tooltips/tooltip-room-left-to-sell';
import type { Occupancy, Period, RoomOccupancy } from 'lumen/models';
import { calculateTotalRoomsForSale } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

/* A room's column carries the room's PMS id, like the Overbookings and
   Restrictions columns, and the cell reads the room's values with it. It also
   carries the room name, as the group header shows it, for the hovercard. */
export interface LeftToSellMonthColumn extends PeriodMonthColumn {
  roomName?: string;
}

type DataRequestService = Services['pricing-assistant-data-request-service'];

/* The values the room's hovercard shows, besides the date. */
interface RoomLeftToSellHovercard {
  leftToSell: number;
  overbooking: number;
  roomName: string;
}

interface HotelPricingAssistantTableCellsTdLeftToSellMonthSignature {
  Args: {
    column: LeftToSellMonthColumn;
    value: Period;
  };
}

export default class HotelPricingAssistantTableCellsTdLeftToSellMonth extends Component<HotelPricingAssistantTableCellsTdLeftToSellMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service('pricing-assistant-data-request-service')
  declare pricingAssistantDataRequestService: Services['pricing-assistant-data-request-service'];

  /* A failed request leaves no record for the date, so the cell shows the missing state. */
  get availableRooms(): number | null {
    return this.occupancy?.availableRooms ?? null;
  }

  get displayValue(): number | string {
    return this.roomsForSale ?? this.intl.t('general.missing');
  }

  /* Only a room's cell has a hovercard; the hotel level column carries no room name. */
  get hovercard(): RoomLeftToSellHovercard | null {
    const { roomName } = this.args.column;

    if (!roomName || this.roomsForSale == null || this.overbooking == null) {
      return null;
    }

    return {
      leftToSell: this.roomsForSale,
      overbooking: this.overbooking,
      roomName,
    };
  }

  /* The overbooking needs both restrictions requests, as the Rate groups cell does.
     Rooms have no overbooking pushes yet. */
  get isLoading(): boolean {
    const { restrictionsRecommendationsRequestTask, restrictionsRequestTask } =
      this.pricingAssistantDataRequestService;
    const isHotelOverbookingPushing =
      !this.isRoom && this.args.value.hasOverbookingActionInProgress;

    return (
      this.occupancyRequestTask.isRunning ||
      restrictionsRequestTask.isRunning ||
      restrictionsRecommendationsRequestTask.isRunning ||
      isHotelOverbookingPushing
    );
  }

  get isMissing(): boolean {
    return this.roomsForSale == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.value.fromDate);
  }

  get isRoom(): boolean {
    return this.args.column.pmsRoomId !== undefined;
  }

  /* The hotel's availability record for the date, or the room's. */
  get occupancy(): Occupancy | RoomOccupancy | undefined {
    const { pmsRoomId } = this.args.column;
    const { day } = this.args.value;

    return pmsRoomId === undefined
      ? day.occupancy
      : day.roomOccupancy(pmsRoomId);
  }

  /* The request behind the available rooms: the room availability for a room,
     the general data (which holds the OTB) for the hotel level. */
  get occupancyRequestTask():
    | DataRequestService['pricingAssistantGeneralDataRequestsTask']
    | DataRequestService['roomOccupancyRequestTask'] {
    const {
      pricingAssistantGeneralDataRequestsTask,
      roomOccupancyRequestTask,
    } = this.pricingAssistantDataRequestService;

    return this.isRoom
      ? roomOccupancyRequestTask
      : pricingAssistantGeneralDataRequestsTask;
  }

  /* TODO: read the room's overbooking override, like the hotel level, once
     room-level overbooking overrides exist. */
  get overbooking(): number | null {
    const { pmsRoomId } = this.args.column;

    return pmsRoomId !== undefined
      ? this.args.value.roomOverbooking(pmsRoomId)
      : this.args.value.effectiveOverbookingWithOverride;
  }

  get roomsForSale(): number | null {
    if (this.availableRooms == null || this.overbooking == null) {
      return null;
    }

    return calculateTotalRoomsForSale(this.availableRooms, this.overbooking);
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      {{! Focusable so keyboard users can open the hovercard }}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id={{if
          this.isRoom
          "td-room-left-to-sell-month"
          "td-hotel-left-to-sell-month"
        }}
        tabindex={{if this.hovercard "0"}}
      >
        {{this.displayValue}}
        {{#if this.hovercard}}
          <HotelPricingAssistantTableTooltipsTooltipRoomLeftToSell
            @date={{@value.fromDate}}
            @leftToSell={{this.hovercard.leftToSell}}
            @overbooking={{this.hovercard.overbooking}}
            @roomName={{this.hovercard.roomName}}
          />
        {{/if}}
      </div>
    {{/if}}
  </template>
}
